import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CollapsiblePanel } from '@ge/components/collapsible-panel';
import { Icon, Icons } from '@ge/components/icon';
import { Capability } from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { isActiveDelays } from '@ge/shared/util/time-date';
import { typography } from '@ge/tokens';

const DelaySummary = styled.div`
  flex: 3;
  gap: 10px;
  opacity: 0px;
`;

const AddDelayButton = styled.button`
  color: ${(props) => props.theme.entityDetails.notes.addNote};
  display: flex;
  font-weight: ${typography.weight.bold};
  text-align: left;
  text-transform: uppercase;
  width: 115px;
  position: absolute;
  left: 5px;
  top: 5px;
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Text = styled.div`
  font-size: 12px;
  font-weight: 300;
  line-height: 13.2px;
  letter-spacing: 0.5px;
  display: flex;
  position: relative;
  margin-right: 6px;
`;

const Days = styled.div`
  display: flex;
  align-items: baseline;
  font-family: Museo Sans;
  font-size: 24px;
  font-weight: 400;
  line-height: 28.8px;
  letter-spacing: -0.5px;
  text-align: left;
`;

const DaysText = styled.div`
  position: absolute;
  top: -9px;
  left: 111px;
  color: #b3b3b3;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.5px;
  text-align: left;
`;

const AddIcon = styled(Icon).attrs((props) => ({
  size: 10,
  icon: Icons.ADD,
  color: props.theme.entityDetails.notes.addNote,
}))`
  flex-shrink: 0;
  margin: 1px 7px 0;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  margin: auto;
  overflow: hidden;
  margin: 0 15px 0 0;
`;

const Column = styled.div`
  display: flex;
  position: relative;
  padding-bottom: 15px;

  &:last-child {
    border-right: none;
  }

  &.leftmargin {
    margin-left: 45px;
  }

  &.small {
    flex: 1;
  }

  &.medium {
    flex: 2;
  }

  &.large {
    flex: 3;
  }

  &.button {
    padding-top: 10px;
  }

  &.border {
    border-bottom: 1px solid #1c252d;
  }

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #ccc;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: none !important;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 8px;
  border: none !important;
  color: ${(props) => props.theme.delaysModal.color};
  font-family: Museo Sans;
  font-size: 11px;
  font-weight: 400;
  line-height: 13.2px;
`;

const TableRow = styled.tr``;

const TableCell = styled.td`
  padding: 8px 23px 0 8px;
  text-align: right;
  :nth-child(1) {
    text-align: left;
  }
`;

const StyledCollapsiblePanel = styled(CollapsiblePanel)`
  padding: 4px 12px;
  border-radius: 4px;
  .title {
    border-bottom: 0;
    color: ${({ theme }) => theme.analyze.filter.collapseHeader};
    margin: 8px;
    padding: 0;
    h2 {
      font: 600 12px/13px 'Museo Sans';
      letter-spacing: 0.5px;
      margin-left: 5px;
      text-align: left;
    }
  }
  .carot {
    fill: ${({ theme }) => theme.analyze.filter.collapseArrow};
    padding: 0;
  }
  .collapsible-panel-content {
    button {
      color: ${({ theme }) => theme.analyze.filter.showMoreButton};
      font: 300 12px/15px;
      margin: -8px 0 8px 2px;
      padding: 0;
      text-decoration: underline;
    }
  }
`;

const DetailWrapper = styled.div`
  width: 65%;
  position: relative;
  display: flex;
  padding-right: 45px;

  &.small {
    width: 35%;
    padding: 0 10px 0 14px;
  }
`;

const Divider = styled.div`
  border-left: 1px solid #38434e;
  height: 40px;
  left: 50%;
  margin-left: -7px;
`;

const NotesEmpty = styled.div`
  flex: 4;
  border-bottom: 1px solid ${(props) => props.theme.entityDetails.notes.separator};
  p {
    font-size: 12px;
    line-height: 15px;
    margin: 0;
    padding-bottom: 25px;
  }
`;

export const TaskDelayHeader = ({
  handleChange,
  delayDetails,
  getDelayDuration,
  isDisabledReopenTask,
}) => {
  const { t } = useTranslation(['entity-details']);
  const [filteredDelays, setFilteredDelays] = useState(null);
  const [delaySummary, setDelaySummary] = useState([]);
  const [delayDays, setDelayDays] = useState(0);

  const capitalizeFirst = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const generateDelaySummary = () => {
    const delays = [],
      summary = {};
    let total_delay_days = 0;
    delayDetails?.forEach((e, i) => {
      const duration = getDelayDuration(t, e, true) || 0;
      const { reason, reasonDesc, endDate, type } = e;
      let key = type ? `${reason}_${type}` : reason;
      total_delay_days += duration;
      if (isActiveDelays(endDate)) delays.push({ ...e, index: i });
      if (!summary[key])
        summary[key] = {
          duration,
          reason: reasonDesc,
          count: 1,
          type: type ? capitalizeFirst(type) : null,
        };
      else if (summary[key]) {
        summary[key].count = summary[key].count + 1;
        summary[key].duration = summary[key].duration + duration;
      }
    });
    setDelayDays(total_delay_days);
    setDelaySummary(summary);
    setFilteredDelays(delays.length);
  };

  useEffect(() => {
    if (delayDetails?.length) generateDelaySummary();
  }, [delayDetails]);

  return (
    <Container>
      <Column className="small button">
        <AuthRender
          capability={Capability.FIELD_TASKS}
          create
          description="Add Delay"
          siteLevel={false}
        >
          <AddDelayButton onClick={handleChange} disabled={isDisabledReopenTask}>
            <AddIcon />
            {t('notes.delays.add_delay', 'ADD DELAY')}
          </AddDelayButton>
        </AuthRender>
      </Column>
      {!delayDetails?.length ? (
        <NotesEmpty>
          <p>{t('notes.no_notes', 'No information added.')}</p>
        </NotesEmpty>
      ) : (
        <>
          <Column className="medium border leftmargin">
            <DetailWrapper>
              <Text>{t('notes.delays.delay_by', 'TASK DELAYED BY')}</Text>
              <Days>
                {delayDays}
                <DaysText>{t('notes.delays.days', 'days')}</DaysText>
              </Days>
            </DetailWrapper>
            <Divider />
            <DetailWrapper className="small">
              <Text>{t('notes.delays.open_Delay', 'OPEN DELAYS')}</Text>
              <Days>{filteredDelays}</Days>
            </DetailWrapper>
          </Column>
          <Column className="large border">
            <DelaySummary>
              <StyledCollapsiblePanel
                expanded={false}
                headerContent={<h2>{t('notes.delays.delay_summary', 'DELAY SUMMARY')}</h2>}
              >
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>{t('notes.delays.delay_reason', 'Delay Reason')}</TableHeader>
                      <TableHeader>
                        {t('notes.delays.delay_summary', 'Total Duration (days)')}
                      </TableHeader>
                      <TableHeader>{t('notes.delays.count', 'Count')}</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(delaySummary).map((item, index) => {
                      const { reason, duration, count, type } = delaySummary[item];
                      return (
                        <TableRow key={index}>
                          <TableCell>{type ? `${reason} - ${type}` : reason}</TableCell>
                          <TableCell>{duration}</TableCell>
                          <TableCell>{count}</TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </StyledCollapsiblePanel>
            </DelaySummary>
          </Column>
        </>
      )}
    </Container>
  );
};
export default TaskDelayHeader;

TaskDelayHeader.propTypes = {
  delayDetails: PropTypes.array.isRequired,
  handleChange: PropTypes.func,
  getDelayDuration: PropTypes.func,
  isDisabledReopenTask: PropTypes.bool,
};
